// Utils
import { defaultExamTypes } from "src/utils/emr/examTypeHelper";
import {
  DEFAULT_POLL_INTERVAL_MINUTES,
  DEFAULT_WORKLIST_TIMING,
  Translate,
  WorklistTimingValues,
  buildWorklistTiming,
  worklistTimingFromConfig,
} from "src/utils/emr/worklistTimingHelper";

// Types
import {
  EMRCatalogDeviceType,
  EMRConnectionType,
  EMRExamType,
  EMRHubConnectionSourceKind,
  EMRPacServerJobPayload,
  EMRPacServerType,
  EMRRetrieveMethod,
} from "src/services/emr/types";

export const HUB_AE_TITLE = "EMRHUB";
export const NEW_JOB_RETRIEVE_METHOD: EMRRetrieveMethod = "CGet";
export const UNSET_RETRIEVE_METHOD: EMRRetrieveMethod = "CMove";
export const DEFAULT_DICOM_PORT = "104";
export const DEFAULT_PRIOR_HISTORY_MONTHS = 24;
export const ALL_PRIOR_HISTORY_MONTHS = 240;
export const PRIOR_HISTORY_MONTHS_OPTIONS = [0, 6, 12, 24, 60, 240];
export const DEFAULT_ASSOCIATION_TIMEOUT_SECONDS = "30";

const AE_TITLE_MAX_LENGTH = 16;
const PORT_MIN = 1;
const PORT_MAX = 65535;

export type DicomAddress = {
  host: string;
  port: string;
  remoteAeTitle: string;
  localAeTitle: string;
};

export type ExamDelivery = "Pull" | "Push";

type PacServerJobBase = {
  enabled: boolean;
  connectionId?: string;
  existingConfig?: EMRConnectionType["config"];
  useServerAddress: boolean;
  address: DicomAddress;
};

export type PacServerWorklistJob = PacServerJobBase & {
  timing: WorklistTimingValues;
  scheduledStationAeTitle: string;
};

export type PacServerExamsJob = PacServerJobBase & {
  existingSourceKind?: EMRHubConnectionSourceKind;
  examTypes: Array<EMRExamType>;
  priorHistoryMonths: number;
  pollIntervalMinutes: number;
  delivery: ExamDelivery;
  retrieveMethod: EMRRetrieveMethod;
  listenPort: string;
  moveListenerPort: string;
};

export type PacServerReportsJob = PacServerJobBase & {
  associationTimeoutSeconds: string;
  exportFrom: string;
};

export type PacServerFormValues = {
  name: string;
  deviceKey: string;
  address: DicomAddress;
  worklist: PacServerWorklistJob;
  exams: PacServerExamsJob;
  reports: PacServerReportsJob;
};

export const EMPTY_DICOM_ADDRESS: DicomAddress = {
  host: "",
  port: DEFAULT_DICOM_PORT,
  remoteAeTitle: "",
  localAeTitle: HUB_AE_TITLE,
};

export const isOtherDevice = (device: EMRCatalogDeviceType | undefined) =>
  !device || device.manufacturer === "Other";

export const findDicomPacServerDevice = (
  devices: Array<EMRCatalogDeviceType>
) => {
  const archives = devices.filter((device) => device.kind === "Archive");
  return archives.find(isOtherDevice) ?? archives[0];
};

const padTwo = (value: number) => String(value).padStart(2, "0");

export const toDateInputValue = (date: Date) =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

export const todayDateInputValue = () => toDateInputValue(new Date());

export const dateInputValueFromIso = (iso: string | undefined) =>
  iso ? toDateInputValue(new Date(iso)) : "";

export const isoFromDateInputValue = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const getDeviceDisplayName = (
  device: EMRCatalogDeviceType | undefined
) =>
  !device || isOtherDevice(device)
    ? device?.model ?? ""
    : `${device.manufacturer} ${device.model}`;

export const remoteAeTitleLabel = (
  device: EMRCatalogDeviceType | undefined,
  t: Translate
) =>
  device && !isOtherDevice(device)
    ? t("{{model}} AE title", { model: device.model })
    : t("Remote AE title");

export const suggestRemoteAeTitle = (device: EMRCatalogDeviceType) =>
  isOtherDevice(device)
    ? ""
    : device.model
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, AE_TITLE_MAX_LENGTH);

export const createPacServerFormValues = (
  device: EMRCatalogDeviceType,
  listenPort: string
): PacServerFormValues => {
  const address: DicomAddress = {
    ...EMPTY_DICOM_ADDRESS,
    remoteAeTitle: suggestRemoteAeTitle(device),
  };

  return {
    name: isOtherDevice(device) ? "" : getDeviceDisplayName(device),
    deviceKey: device.key,
    address,
    worklist: {
      enabled: true,
      useServerAddress: true,
      address,
      timing: DEFAULT_WORKLIST_TIMING,
      scheduledStationAeTitle: "",
    },
    exams: {
      enabled: true,
      useServerAddress: true,
      address,
      examTypes: defaultExamTypes(device.examTypes),
      priorHistoryMonths: DEFAULT_PRIOR_HISTORY_MONTHS,
      pollIntervalMinutes: DEFAULT_POLL_INTERVAL_MINUTES,
      delivery: "Pull",
      retrieveMethod: NEW_JOB_RETRIEVE_METHOD,
      listenPort,
      moveListenerPort: listenPort,
    },
    reports: {
      enabled: false,
      useServerAddress: true,
      address,
      associationTimeoutSeconds: DEFAULT_ASSOCIATION_TIMEOUT_SECONDS,
      exportFrom: todayDateInputValue(),
    },
  };
};

const addressFromConfig = (
  config: EMRConnectionType["config"] | undefined
): DicomAddress => ({
  host: config?.host ?? "",
  port: config?.port !== undefined ? String(config.port) : DEFAULT_DICOM_PORT,
  remoteAeTitle: config?.remoteAeTitle ?? "",
  localAeTitle: config?.localAeTitle ?? HUB_AE_TITLE,
});

export const isSameAddress = (left: DicomAddress, right: DicomAddress) =>
  left.host.trim().toLowerCase() === right.host.trim().toLowerCase() &&
  left.port.trim() === right.port.trim() &&
  left.remoteAeTitle.trim() === right.remoteAeTitle.trim() &&
  left.localAeTitle.trim() === right.localAeTitle.trim();

export const findPacServerJob = (
  pacServer: EMRPacServerType,
  kinds: Array<EMRHubConnectionSourceKind>
) => pacServer.jobs.find((job) => kinds.includes(job.sourceKind));

export const pacServerFormValuesFromServer = (
  pacServer: EMRPacServerType,
  device: EMRCatalogDeviceType | undefined,
  listenPort: string
): PacServerFormValues => {
  const worklistJob = findPacServerJob(pacServer, ["Worklist"]);
  const examsJob = findPacServerJob(pacServer, [
    "DicomQueryRetrieve",
    "DicomListener",
  ]);
  const reportsJob = findPacServerJob(pacServer, ["DicomStore"]);
  const isPush = examsJob?.sourceKind === "DicomListener";

  const firstWithAddress = [
    worklistJob,
    isPush ? undefined : examsJob,
    reportsJob,
  ].find((job) => job?.config?.host);
  const address = firstWithAddress
    ? addressFromConfig(firstWithAddress.config)
    : {
        ...EMPTY_DICOM_ADDRESS,
        remoteAeTitle:
          examsJob?.config?.allowedCallingAeTitles?.[0] ??
          (device ? suggestRemoteAeTitle(device) : ""),
        localAeTitle: examsJob?.config?.aeTitle ?? HUB_AE_TITLE,
      };

  const jobAddress = (job: EMRConnectionType | undefined) =>
    job?.config?.host ? addressFromConfig(job.config) : address;

  const examsAddress = isPush
    ? {
        ...address,
        remoteAeTitle:
          examsJob?.config?.allowedCallingAeTitles?.[0] ??
          address.remoteAeTitle,
        localAeTitle: examsJob?.config?.aeTitle ?? address.localAeTitle,
      }
    : jobAddress(examsJob);

  return {
    name: pacServer.name,
    deviceKey: pacServer.deviceKey,
    address,
    worklist: {
      enabled: Boolean(worklistJob?.enabled),
      connectionId: worklistJob?.connectionId,
      existingConfig: worklistJob?.config,
      useServerAddress: isSameAddress(jobAddress(worklistJob), address),
      address: jobAddress(worklistJob),
      timing: worklistJob
        ? worklistTimingFromConfig(worklistJob.config)
        : DEFAULT_WORKLIST_TIMING,
      scheduledStationAeTitle:
        worklistJob?.config?.scheduledStationAeTitle ?? "",
    },
    exams: {
      enabled: Boolean(examsJob?.enabled),
      connectionId: examsJob?.connectionId,
      existingConfig: examsJob?.config,
      existingSourceKind: examsJob?.sourceKind,
      useServerAddress: isSameAddress(examsAddress, address),
      address: examsAddress,
      examTypes:
        examsJob?.config?.examTypes ??
        defaultExamTypes(device?.examTypes ?? []),
      priorHistoryMonths:
        examsJob?.sourceKind === "DicomQueryRetrieve"
          ? examsJob.config?.priorHistoryMonths ?? ALL_PRIOR_HISTORY_MONTHS
          : DEFAULT_PRIOR_HISTORY_MONTHS,
      pollIntervalMinutes:
        examsJob?.sourceKind === "DicomQueryRetrieve"
          ? examsJob.config?.pollIntervalMinutes ??
            DEFAULT_POLL_INTERVAL_MINUTES
          : DEFAULT_POLL_INTERVAL_MINUTES,
      delivery: isPush ? "Push" : "Pull",
      retrieveMethod:
        examsJob?.sourceKind === "DicomQueryRetrieve"
          ? examsJob.config?.retrieveMethod ?? UNSET_RETRIEVE_METHOD
          : NEW_JOB_RETRIEVE_METHOD,
      listenPort: isPush ? String(examsJob?.config?.port ?? "") : listenPort,
      moveListenerPort:
        examsJob?.sourceKind === "DicomQueryRetrieve"
          ? String(examsJob.config?.listenerPort ?? "")
          : listenPort,
    },
    reports: {
      enabled: Boolean(reportsJob?.enabled),
      connectionId: reportsJob?.connectionId,
      existingConfig: reportsJob?.config,
      useServerAddress: isSameAddress(jobAddress(reportsJob), address),
      address: jobAddress(reportsJob),
      associationTimeoutSeconds: String(
        reportsJob?.config?.associationTimeoutSeconds ??
          DEFAULT_ASSOCIATION_TIMEOUT_SECONDS
      ),
      exportFrom: reportsJob
        ? dateInputValueFromIso(reportsJob.config?.exportFrom)
        : todayDateInputValue(),
    },
  };
};

const parsePort = (text: string) => {
  const port = Number(text);
  return text.trim() !== "" &&
    Number.isInteger(port) &&
    port >= PORT_MIN &&
    port <= PORT_MAX
    ? port
    : null;
};

const isValidAeTitle = (aeTitle: string) =>
  aeTitle.trim().length > 0 && aeTitle.trim().length <= AE_TITLE_MAX_LENGTH;

export const buildDicomAddress = (address: DicomAddress) => {
  const port = parsePort(address.port);

  if (
    !address.host.trim() ||
    port === null ||
    !isValidAeTitle(address.remoteAeTitle) ||
    !isValidAeTitle(address.localAeTitle)
  ) {
    return null;
  }

  return {
    localAeTitle: address.localAeTitle.trim(),
    remoteAeTitle: address.remoteAeTitle.trim(),
    host: address.host.trim(),
    port,
  };
};

const resolveAddress = (job: PacServerJobBase, serverAddress: DicomAddress) =>
  job.useServerAddress ? serverAddress : job.address;

const DERIVED_FROM_EXAM_TYPES = ["modalities", "acceptedSopClasses"];

export const keepExistingConfig = (
  existingConfig: EMRConnectionType["config"] | undefined,
  config: Record<string, unknown>
) => {
  if (!existingConfig) return config;

  const derivedKeys = "examTypes" in config ? DERIVED_FROM_EXAM_TYPES : [];
  const kept = Object.fromEntries(
    Object.entries(existingConfig).filter(([key]) => !derivedKeys.includes(key))
  );

  return { ...kept, ...config };
};

const keepExisting = (
  job: PacServerJobBase,
  sameKind: boolean,
  config: Record<string, unknown>
) => (sameKind ? keepExistingConfig(job.existingConfig, config) : config);

const withoutExamTypeFilter = (
  config: EMRConnectionType["config"] | undefined
) =>
  config &&
  Object.fromEntries(
    Object.entries(config).filter(
      ([key]) => key !== "examTypes" && !DERIVED_FROM_EXAM_TYPES.includes(key)
    )
  );

const buildWorklistJob = (
  values: PacServerFormValues
): EMRPacServerJobPayload | null => {
  const job = values.worklist;
  const address = buildDicomAddress(resolveAddress(job, values.address));
  const timing = buildWorklistTiming(job.timing);
  const scheduledStationAeTitle = job.scheduledStationAeTitle.trim();

  if (
    !address ||
    !timing ||
    scheduledStationAeTitle.length > AE_TITLE_MAX_LENGTH
  ) {
    return null;
  }

  const filterByExamTypes =
    values.exams.enabled && values.exams.examTypes.length > 0;

  const config: Record<string, unknown> = {
    mode: "DicomWorklist",
    ...address,
    ...timing,
    scheduledStationAeTitle: scheduledStationAeTitle || null,
    ...(filterByExamTypes && { examTypes: values.exams.examTypes }),
  };

  const existingConfig = filterByExamTypes
    ? job.existingConfig
    : withoutExamTypeFilter(job.existingConfig);

  return {
    ...(job.connectionId && { connectionId: job.connectionId }),
    sourceKind: "Worklist",
    configJson: keepExistingConfig(existingConfig, config),
    enabled: job.enabled,
  };
};

const buildExamsJob = (
  values: PacServerFormValues
): EMRPacServerJobPayload | null => {
  const job = values.exams;
  if (job.examTypes.length === 0) return null;

  const sourceKind: EMRHubConnectionSourceKind =
    job.delivery === "Push" ? "DicomListener" : "DicomQueryRetrieve";
  const sameKind = job.existingSourceKind === sourceKind;
  const connectionId = sameKind ? job.connectionId : undefined;
  const address = resolveAddress(job, values.address);

  let config: Record<string, unknown>;

  if (sourceKind === "DicomListener") {
    const port = parsePort(job.listenPort);
    if (
      port === null ||
      !isValidAeTitle(address.localAeTitle) ||
      !isValidAeTitle(address.remoteAeTitle)
    ) {
      return null;
    }

    config = {
      aeTitle: address.localAeTitle.trim(),
      port,
      allowedCallingAeTitles: [address.remoteAeTitle.trim()],
      examTypes: job.examTypes,
    };
  } else {
    const dicomAddress = buildDicomAddress(address);
    if (!dicomAddress) return null;

    const isLegacyUnset =
      sameKind &&
      !job.existingConfig?.retrieveMethod &&
      job.moveListenerPort.trim() === "";
    if (
      job.retrieveMethod === "CMove" &&
      !isLegacyUnset &&
      parsePort(job.moveListenerPort) === null
    ) {
      return null;
    }

    config = {
      ...dicomAddress,
      examTypes: job.examTypes,
      priorHistoryMonths: job.priorHistoryMonths,
      pollIntervalMinutes: job.pollIntervalMinutes,
    };
  }

  const configJson = keepExisting(job, sameKind, config);

  return {
    ...(connectionId && { connectionId }),
    sourceKind,
    configJson:
      sourceKind === "DicomQueryRetrieve"
        ? withRetrieveMethod(
            configJson,
            job.retrieveMethod,
            parsePort(job.moveListenerPort)
          )
        : configJson,
    enabled: job.enabled,
  };
};

const withRetrieveMethod = (
  config: Record<string, unknown>,
  retrieveMethod: EMRRetrieveMethod,
  listenerPort: number | null
) => {
  const {
    moveDestinationAeTitle,
    listenerPort: _listenerPort,
    ...rest
  } = config;

  if (retrieveMethod === "CGet") return { ...rest, retrieveMethod };
  if (listenerPort === null) return config;

  return {
    ...rest,
    retrieveMethod,
    moveDestinationAeTitle: config.localAeTitle,
    listenerPort,
  };
};

const buildReportsJob = (
  values: PacServerFormValues,
  device: EMRCatalogDeviceType | undefined
): EMRPacServerJobPayload | null => {
  const job = values.reports;
  const address = buildDicomAddress(resolveAddress(job, values.address));
  const timeout = Number(job.associationTimeoutSeconds);
  const exportFrom = job.exportFrom
    ? isoFromDateInputValue(job.exportFrom)
    : undefined;

  if (
    !address ||
    !Number.isInteger(timeout) ||
    timeout < 5 ||
    timeout > 300 ||
    exportFrom === null
  ) {
    return null;
  }

  const config: Record<string, unknown> = {
    ...address,
    reportTypes: ["Dicom"],
    profile: device?.storeProfile ?? "Generic",
    associationTimeoutSeconds: timeout,
    ...(exportFrom && { exportFrom }),
  };

  return {
    ...(job.connectionId && { connectionId: job.connectionId }),
    sourceKind: "DicomStore",
    configJson: keepExisting(job, true, config),
    enabled: job.enabled,
  };
};

export const buildPacServerJobs = (
  values: PacServerFormValues,
  device: EMRCatalogDeviceType | undefined
): Array<EMRPacServerJobPayload> | null => {
  const builders: Array<
    [PacServerJobBase, () => EMRPacServerJobPayload | null]
  > = [
    [values.worklist, () => buildWorklistJob(values)],
    [values.exams, () => buildExamsJob(values)],
    [values.reports, () => buildReportsJob(values, device)],
  ];

  const jobs: Array<EMRPacServerJobPayload> = [];

  for (const [job, build] of builders) {
    if (!job.enabled && !job.connectionId) continue;

    const payload = build();
    if (!payload) return null;
    jobs.push(payload);
  }

  return jobs.some((job) => job.enabled) ? jobs : null;
};

export const needsWorklist = (
  values: PacServerFormValues,
  hubHasOtherWorklist: boolean
) =>
  values.exams.enabled &&
  values.exams.delivery === "Pull" &&
  !values.worklist.enabled &&
  !hubHasOtherWorklist;
